import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getMindmap,
  buildMindmapBatch,
  analyzeTopics,
  listTopicAnalyses,
  deleteTopicAnalysis,
  translateText,
  getTranslateProviders,
  getTranslateConfig,
  translateAnswer,
  listTranslations,
  deleteTranslation,
  listVoices,
  startNarration,
  getNarration,
  truthLens,
  checkLinkHealth,
  buildCodeGraph,
  listCodeRepos,
  unbindCodeRepo,
  runImpactAnalysis,
  listImpactHistory,
  getImpactRun,
  deleteImpactRun,
  codeOverview,
  codeModuleGraph,
  codeApiSurface,
  codeTrace,
  codeSymbolLookup,
  generateCodeDocs,
} from '../services/researchApi'

// Code explorer (doc-gen tools). Queries keyed by repo+params, cached — the
// data only changes when the repo is re-analyzed.
export const useCodeOverview = (repo_path, enabled) =>
  useQuery({
    queryKey: ['code', 'overview', repo_path],
    queryFn: () => codeOverview({ repo_path, depth: 3, top: 5 }),
    enabled: !!repo_path && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
export const useCodeModuleGraph = (repo_path, enabled) =>
  useQuery({
    queryKey: ['code', 'module-graph', repo_path],
    queryFn: () => codeModuleGraph({ repo_path, depth: 3, cap: 40 }),
    enabled: !!repo_path && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
export const useCodeApiSurface = (repo_path, enabled) =>
  useQuery({
    queryKey: ['code', 'api-surface', repo_path],
    queryFn: () => codeApiSurface({ repo_path }),
    enabled: !!repo_path && enabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
export const useCodeTrace = () => useMutation({ mutationFn: codeTrace })
export const useCodeSymbol = () => useMutation({ mutationFn: codeSymbolLookup })
export const useGenerateCodeDocs = () => useMutation({ mutationFn: generateCodeDocs })

// C2 — impact analysis: change notes weighed against an indexed codebase.
export function useImpactAnalysis() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: runImpactAnalysis,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['research', 'impactHistory'] }),
  })
}
export const useImpactHistory = () =>
  useQuery({ queryKey: ['research', 'impactHistory'], queryFn: () => listImpactHistory(20), staleTime: 30000 })
export const useImpactRun = () => useMutation({ mutationFn: getImpactRun })
export function useDeleteImpactRun() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteImpactRun,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['research', 'impactHistory'] }),
  })
}

// Truth lens: weigh an answer's key claims against the corpus (LLM-heavy,
// on-demand; the engine caches per claim, so re-running an answer is cheap).
export const useTruthLens = () => useMutation({ mutationFn: truthLens })
// Link health: on-demand probe of every indexed source URL in a project.
export const useLinkHealth = () => useMutation({ mutationFn: checkLinkHealth })
// Code graph build (Neo4j) — enables the "Graph RAG" ask strategy for a repo.
export const useBuildCodeGraph = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: buildCodeGraph,
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['codeRepos', vars.project] }),
  })
}
// Per-project repo binding (Graph RAG scoping): list + unbind.
export const useCodeRepos = (project) =>
  useQuery({
    queryKey: ['codeRepos', project],
    queryFn: () => listCodeRepos(project),
    enabled: !!project,
  })
export const useUnbindCodeRepo = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: unbindCodeRepo,
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['codeRepos', vars.project] }),
  })
}

export function useMindmap(project, includeData = true) {
  return useQuery({
    queryKey: ['research', 'mindmap', project, includeData],
    queryFn: () => getMindmap({ project, includeData }),
    enabled: !!project,
    staleTime: 30 * 1000,
  })
}

export function useBuildMindmap() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: buildMindmapBatch,
    onSuccess: (_d, vars) =>
      qc.invalidateQueries({ queryKey: ['research', 'mindmap', vars.project] }),
  })
}

export function useAnalyzeTopics() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: analyzeTopics,
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['research', 'topicHistory', vars.project] }),
  })
}

export function useTopicHistory(project) {
  return useQuery({
    queryKey: ['research', 'topicHistory', project],
    queryFn: () => listTopicAnalyses({ project }),
    enabled: !!project,
  })
}

export function useDeleteTopicAnalysis() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteTopicAnalysis,
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['research', 'topicHistory', vars.project] }),
  })
}

// Which translation providers exist right now (light always; t8r when its
// backend is wired). `default` is the saved Settings preference, clamped to an
// available provider.
export function useTranslateProviders() {
  return useQuery({
    queryKey: ['translate', 'providers'],
    queryFn: getTranslateProviders,
    staleTime: 30000,
  })
}

// Effective light-translation LLM config (provider/model + available providers)
// for the Settings picker.
export function useTranslateConfig() {
  return useQuery({
    queryKey: ['translate', 'config'],
    queryFn: getTranslateConfig,
    staleTime: 30000,
  })
}

export function useTranslate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: translateText,
    onSuccess: (_d, vars) => {
      if (vars?.save && vars?.project) qc.invalidateQueries({ queryKey: ['research', 'translationHistory', vars.project] })
    },
  })
}

export function useTranslateAnswer() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: translateAnswer,
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['rag', 'history', vars.project] }),
  })
}

export function useTranslationHistory(project) {
  return useQuery({
    queryKey: ['research', 'translationHistory', project],
    queryFn: () => listTranslations({ project }),
    enabled: !!project,
  })
}

export function useDeleteTranslation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: deleteTranslation,
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ['research', 'translationHistory', vars.project] }),
  })
}

export function useVoices(language) {
  return useQuery({
    queryKey: ['research', 'voices', language],
    queryFn: () => listVoices({ language }),
    staleTime: Infinity,
  })
}

export function useStartNarration() {
  return useMutation({ mutationFn: startNarration })
}

// Poll a narration job until it reaches a terminal state.
export function useNarrationStatus(jobId, enabled) {
  return useQuery({
    queryKey: ['research', 'narration', jobId],
    queryFn: () => getNarration(jobId),
    enabled: !!jobId && enabled,
    refetchInterval: (q) => {
      const s = q.state.data?.status
      return s === 'completed' || s === 'failed' || s === 'cancelled' ? false : 1500
    },
  })
}
